import { Box, Button, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";

import { useLangContext } from "../../../../context/langContext";

import { dateFormatter } from "../../../../utils/date-formatter";
import CustomTable from "../../../common/tables/Table";
import errorResolver from "../../../../utils/error-resolver";
import { useNotificationContext } from "../../../../context/notificationContext";
import {
  PbxTrunkListColumnFilterable,
  PbxTrunksListRequest,
} from "../../../../services/Endpoints/pbxTrunk/PbxTrunkListEndpoint";
import { useProtectedAxiosInstance } from "../../../../hooks/axiosInstance";
import { FilterOperatorEnum } from "../../../../services/Schemas/ListQueryString";
import EditAction from "../../../common/edit-action/EditAction";
import { PbxTrunkSchema } from "../../../../services/Schemas/PbxTrunk";
import DeleteAction from "../../../common/delete-action/DeleteAction";
import { PbxTrunkDeleteRequest } from "../../../../services/Endpoints/pbxTrunk/PbxTrunkDeleteEndpoint";
import AddNewTrunkModal from "./add-new-trunk-modal/AddNewTrunk";
import { AccessEnums } from "../../../../constants/accessEnum";
import { useCanAccess } from "../../../../hooks/canAccess";
import { ITableSchema } from "../../../../utils/types";
import { useLocation } from "react-router-dom";

interface IBroadcastTabsProps {
  id: number | string;
  handleReload: () => void;
  reload: boolean;
}

export default function PbxTrunkTabsComponent({
  id,
  handleReload,
  reload,
}: Readonly<IBroadcastTabsProps>) {
  const [value, setValue] = useState("1");
  const { lang, dir } = useLangContext();
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [data, setData] = useState<any[]>([]);
  const { setNotification } = useNotificationContext();
  const [trunkModalData, setTrunkModalData] = useState<
    PbxTrunkSchema | undefined
  >(undefined);
  const [trunkModalOpen, setTrunkModalOpen] = useState(false);
  const location = useLocation();

  const protectedInstance = useProtectedAxiosInstance();
  const handleEdit = (trunk: PbxTrunkSchema) => {
    setTrunkModalData(trunk);
    setTrunkModalOpen(true);
  };

  const handleDelete = async (id: number) => {
    const pbxDeleteRequest = new PbxTrunkDeleteRequest(protectedInstance);
    try {
      const resp = await pbxDeleteRequest.send(id);
      setNotification({ type: "success", message: "ترانک با موفقیت حذف شد." });
    } catch (error) {
      console.error(error);
      errorResolver({ error, setNotification });
    }
  };
  const { can } = useCanAccess();

  const getData = async () => {
    const trunkListRequest = new PbxTrunksListRequest(protectedInstance);
    try {
      const resp = await trunkListRequest.get({
        filters: [
          {
            column: PbxTrunkListColumnFilterable.PBX_SERVER_ID,
            operator: FilterOperatorEnum.EQUAL,
            value: id,
          },
        ],
        page,
        count: rowsPerPage,
      });
      setTotalCount(resp.data.total);
      const trunks = resp.data.pbx_trunks.map((trunk) => ({
        ...trunk,
        operations: (
          <Stack direction="row" justifyContent="center" alignItems="center">
            <EditAction
              disabled={!can(AccessEnums.AdminPbxTrunksUpdate)}
              handleEdit={() => handleEdit(trunk)}
            />
            <DeleteAction
              disabled={!can(AccessEnums.AdminPbxTrunksDelete)}
              handleDelete={() => {
                handleDelete(trunk.id);
                handleReload();
              }}
            />
          </Stack>
        ),
      }));
      setData(trunks);
    } catch (error) {
      console.error(error);
      errorResolver({ error, setNotification });
    }
  };

  useEffect(() => {
    getData();
  }, [value, page, rowsPerPage, reload, location.search]);

  const columns: ITableSchema[] = [
    {
      key: "id",
      label: "شناسه",
      minWidth: 120,
      align: "center",
    },
    {
      key: "pbx_server_id",
      label: "شناسه سرور pbx",
      minWidth: 170,
      align: "center",
    },
    {
      key: "pbx_trunk_reference",
      label: "رفرنس ترانک pbx",
      minWidth: 150,
      align: "center",
    },
    {
      key: "caption",
      label: "نام",
      minWidth: 170,
      align: "center",
    },
    {
      key: "description",
      label: "توضیحات",
      minWidth: 170,
      align: "center",
    },
    {
      key: "updated_at",
      label: "تاریخ آپدیت",
      minWidth: 100,
      align: "center",
      format: (value: string) => value && dateFormatter({ lang, date: value }),
    },

    {
      key: "workspace_id",
      label: "آیدی تخته",
      minWidth: 170,
      align: "center",
    },
    {
      key: "operations",
      label: "عملیات",
      minWidth: 170,
      align: "center",
    },
  ];

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(1);
  };

  return (
    <Box sx={{ width: "100%", typography: "body1", backgroundColor: "white" }}>
      <Box
        sx={{ borderBottom: 1, borderColor: "divider", width: "100%" }}
      ></Box>
      <Stack
        direction="row"
        width="100%"
        justifyContent="space-between"
        my="20px"
      >
        <Typography variant="h6DemiBold">لیست ترانکها </Typography>
        <Button
          variant="outlined"
          disabled={!can(AccessEnums.AdminPbxTrunksCreate)}
          onClick={() => setTrunkModalOpen(true)}
        >
          ایجاد ترانک جدید
        </Button>
      </Stack>
      <CustomTable
        hasTopPagination
        hasSearchFilter
        columns={columns}
        count={totalCount}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        page={page}
        rows={data}
        rowsPerPage={rowsPerPage}
      />
      <AddNewTrunkModal
        onClose={() => {
          setTrunkModalOpen(false);
          setTrunkModalData(undefined);
        }}
        openModal={trunkModalOpen}
        serverInfo={trunkModalData}
        handleReload={handleReload}
      />
    </Box>
  );
}
